import AbstractScreenMediator from "../../../../../shared/src/ranj/app/ui/AbstractScreenMediator";
import Anchor from "../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import TextFitter from "../../../../lib/html5-software-library/src/ranj/layout/TextFitter";
import RichTextWrapper from "../../app/ui/richtext/RichTextWrapper";
import { getURLParameter } from "../../../../lib/html5-software-library/src/ranj/util/URLUtil";
import Config from "../../app/config/Config";
import ContextEvent from "../../app/core/ContextEvent";
import StartupEvent from "../../app/startup/StartupEvent";
import LanguageEvent from "../startup/events/LanguageEvent";

export default class BaseSplashScreenMediator extends AbstractScreenMediator {

    constructor(target, dispatcher, languagePack, layoutManager, startup, soundManager, config) {
        super(target, dispatcher, languagePack, layoutManager, soundManager);
        this.dispatcher = dispatcher;
        this._startup = startup;
        this._config = config;

        this._setProgress(0);

        this._handlePreloadCompleteBound = this._handlePreloadComplete.bind(this);
        this._handleLoadProgressBound = this._handleLoadProgress.bind(this);
        this._handleLanguageInitializedBound = this._handleLanguageInitialized.bind(this);
    }

    initialize() {
        super.initialize();

        this._instantHideTexts();
        this._dispatcher.addEventListener(LanguageEvent.INITIALIZED, this._handleLanguageInitializedBound);

        this._buttonStartGame = this._createStartGameButton();
        this._buttonStartGame.setVisible(false);

        this._contentPanel9Slice = this.createGenericVerticalSlice9Panel(this._target.textPanel);

        this._exposeStartButton();

        this._startup.addEventListener(StartupEvent.PROGRESS, this._handleLoadProgressBound);
        this._startup.addEventListener(StartupEvent.COMPLETE, this._handlePreloadCompleteBound);

        // Don't show version; we're logging the version in BasePreloadScreenMediator instead.
        this._showVersion(false);
        
        this.anchorObject(this._target.loadingBar, Anchor.B);
        this.anchorObject(this._target.loadingTextField, Anchor.B);
        this.anchorObject(this._target.textPanel, Anchor.T);
    }

    destroy() {
        super.destroy();

        this._dispatcher.removeEventListener(LanguageEvent.INITIALIZED, this._handleLanguageInitializedBound);

        this._startup.removeEventListener(StartupEvent.PROGRESS, this._handleLoadProgressBound);
        this._startup.removeEventListener(StartupEvent.COMPLETE, this._handlePreloadCompleteBound);

        // NB: make sure to delete expose of start button to avoid memory leaks
        delete window.actions?.clickStartButton;
    }

    // Extracted to its own method to allow for overriding.
    _createStartGameButton() {
        return this.createButton(this._target.textPanel.startbutton, this._handleStartButtonClick.bind(this));
    }

    _showVersion(doShow = true) {
        if (this._target.versionTextField) {
            if (!doShow) {
                this._target.versionTextField.visible = false;
                return;
            }

            this.anchorObject(this._target.versionTextField, Anchor.B);

            const version = this._config.getValue(Config.VERSION);
            const commitHash = this._config.getValue(Config.COMMIT_HASH);

            const str = `${version} (${commitHash})`;
            this.applyText(this._target.versionTextField, str);
        }
    }

    _setProgress(progress) {
        const t = this._target.loadingBar.duration;
        const percentage = Math.round(t * progress);
        this._target.loadingBar.gotoAndStop(Math.min(t - 1, percentage));
    }

    _handleLoadProgress({ data }) {
        this._setProgress(data.progress);
    }

    _handlePreloadComplete() {
        this._startup.removeEventListener(StartupEvent.PROGRESS, this._handleLoadProgressBound);
        this._startup.removeEventListener(StartupEvent.COMPLETE, this._handlePreloadCompleteBound);
        this._target.loadingBar.visible = this._target.loadingTextField.visible = false;
        this._buttonStartGame.setVisible(true);

        if (getURLParameter("autostart") == "1") {
            setTimeout(function () { this._handleStartButtonClick(); }.bind(this), 15);
        }
    }

    _handleStartButtonClick() {
        // this._soundManager.initializeStreamChannels("./assets/sounds/silent.mp3");
        // this.playSound(SoundType.SOUND_CLICK);

        this._buttonStartGame.setEnabled(false);
        this._start();
    }
    
    _handleLanguageInitialized() {
        this._showTexts();
    }

    _start() {
        var contextEvent = new ContextEvent(ContextEvent.START_GAME);
        this.dispatcher.dispatch(contextEvent.type, { data: contextEvent });
    }

    // Used for integration testing by Equalture
    _exposeStartButton() {
        window.actions = window.actions || {};
        window.actions.clickStartButton = () => {
            return new Promise((resolve, reject) => {
                const startButtonSkin = this._target.startButton;
                const handleStartButtonClick = this._handleStartButtonClick.bind(this);

                (function waitForStartButtonToBeVisible() {
                    if (startButtonSkin.visible) {
                        handleStartButtonClick();
                        return resolve();
                    }
                    setTimeout(waitForStartButtonToBeVisible, 30);
                })();
            });
        }
    }

    _instantHideTexts() {
        this._target.textPanel.alpha = 0;
    }

    _showTexts() {
        //console.log('[BaseSplashScreenMediator] RTL mode:', this._target.textPanel.titleTextField.rtl);
        
        this._buttonStartGame.setLabel(this.getText("start.button"));
        
        const titleTextFitter = new TextFitter(this._target.textPanel.titleTextfield, { maxFontSize: 144, minFontSize: 40, lineHeightStep: 0, fontStep: 2 });
        titleTextFitter.setText(this.getText("start.title"));

        this.applyText(this._target.textPanel.bodyTextfield, this.getText("start.body"));
        this.applyText(this._target.loadingTextField, this.getText("start.loading"));

        // calculate layout of text
        this._target.textPanel.bodyTextfield.y = this._target.textPanel.titleTextfield.y + this._target.textPanel.titleTextfield.getMeasuredHeight() + 40;
        this._buttonStartGame.skin.y = this._target.textPanel.bodyTextfield.y + this._target.textPanel.bodyTextfield.getMeasuredHeight() + 100;

        this._contentPanel9Slice.update();

        createjs.Tween.get(this._target.textPanel)
            .to({ alpha: 1 }, 650, createjs.Ease.sineInOut);
    }
}